import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface ModalContextType {
  isAnyModalOpen: boolean;
  registerModal: (id: string) => void;
  unregisterModal: (id: string) => void;
  openModal: (id: string) => void;
  closeModal: (id: string) => void;
  isModalOpen: (id: string) => boolean;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [openModals, setOpenModals] = useState<Set<string>>(new Set());
  const [registeredModals, setRegisteredModals] = useState<Set<string>>(new Set());
  const location = useLocation();

  // Clear all modals on navigation
  useEffect(() => {
    if (openModals.size > 0) {
      setOpenModals(new Set());
      document.body.style.overflow = '';
    }
  }, [location.pathname]);

  // Ensure body overflow is managed correctly
  useEffect(() => {
    if (openModals.size > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [openModals.size]);

  const registerModal = (id: string) => {
    setRegisteredModals(prev => {
      const updated = new Set(prev);
      updated.add(id);
      return updated;
    });
  };

  const unregisterModal = (id: string) => {
    setRegisteredModals(prev => {
      const updated = new Set(prev);
      updated.delete(id);
      return updated;
    });
    
    // Also close the modal if it's open
    if (openModals.has(id)) {
      closeModal(id);
    }
  };

  const openModal = (id: string) => {
    setOpenModals(prev => {
      const updated = new Set(prev);
      updated.add(id);
      return updated;
    });
  };

  const closeModal = (id: string) => {
    setOpenModals(prev => {
      const updated = new Set(prev);
      updated.delete(id);
      return updated;
    });
  };

  const isModalOpen = (id: string) => {
    return openModals.has(id);
  };

  return (
    <ModalContext.Provider 
      value={{ 
        isAnyModalOpen: openModals.size > 0,
        registerModal, 
        unregisterModal, 
        openModal, 
        closeModal, 
        isModalOpen 
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};

export default ModalContext; 